import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z, requiredEmailSchema } from '@dcp/shared'
import {
  Modal,
  AsyncButton,
  Button,
  Input,
  Label,
  Alert,
  AlertDescription,
  toast,
} from '@atrvd/saas-ui'
import { useCreateInvitation } from '@/hooks/useOrganizationInvitations'

/**
 * Narrow type for the email-delivery envelope returned by the invitation edge function.
 * The response shape is `{ data: <invitation row>, emailSent: boolean }`.
 * We only need `emailSent` here; `data` is opaque to this form.
 */
type InviteEnvelope = { emailSent?: boolean } | null

/**
 * Zod schema for invitation form validation
 */
const invitationSchema = z.object({
  organization_name: z.string().min(1, 'Organization name is required'),
  admin_email: requiredEmailSchema,
  admin_first_name: z.string().trim().optional().transform(val => val || undefined),
  admin_last_name: z.string().trim().optional().transform(val => val || undefined),
})

/**
 * Raw form field type — admin_email is plain string (pre-transform).
 * Used as TFieldValues for useForm<> so defaultValues and register() accept plain strings.
 * z.output<typeof invitationSchema>['admin_email'] is the branded Email type.
 */
type InvitationFormValues = z.input<typeof invitationSchema>

/**
 * Props for the OrganizationInviteForm component
 */
interface OrganizationInviteFormProps {
  /** Whether the dialog is open */
  open: boolean
  /** Callback when the dialog open state changes */
  onOpenChange: (open: boolean) => void
  /** Callback when invitation is successfully created */
  onSuccess?: () => void
}

/**
 * Modal form for creating new organization invitations
 * Uses react-hook-form with Zod validation
 */
export function OrganizationInviteForm({ open, onOpenChange, onSuccess }: OrganizationInviteFormProps) {
  const createInvitation = useCreateInvitation()

  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<InvitationFormValues>({
    resolver: zodResolver(invitationSchema),
    defaultValues: {
      organization_name: '',
      admin_email: '',
      admin_first_name: '',
      admin_last_name: '',
    },
  })

  /**
   * Handle form submission to create new invitation
   * @param data - Form data from react-hook-form
   */
  const onSubmit = async (data: InvitationFormValues) => {
    try {
      const result = await createInvitation.mutateAsync(data)
      reset()
      onOpenChange(false)
      onSuccess?.()
      // Surface email-delivery failure without blocking the success flow —
      // the invitation was created; the operator can use Resend to retry.
      if ((result as InviteEnvelope)?.emailSent === false) {
        toast.error('Invitation created, but the email could not be sent. Use Resend to retry.')
      }
    } catch (error) {
      setError('root', {
        message: error instanceof Error ? error.message : 'Failed to create invitation',
      })
    }
  }

  /**
   * Handle modal close - reset form on close
   */
  const handleClose = () => {
    reset()
    onOpenChange(false)
  }

  return (
    <Modal
      isOpen={open}
      onClose={handleClose}
      title="Invite New Tenant"
      description="Send an invitation to create a new tenant organization. An email will be sent to the admin user with a link to set up their account."
      size="sm"
      footer={
        <div className="flex justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <AsyncButton
            type="submit"
            onClick={handleSubmit(onSubmit)}
            isLoading={isSubmitting}
            loadingText="Sending Invitation..."
          >
            Send Invitation
          </AsyncButton>
        </div>
      }
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {errors.root && (
            <Alert variant="destructive">
              <AlertDescription>{errors.root.message}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="organization_name">
                Organization Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="organization_name"
                placeholder="Acme Corporation"
                {...register('organization_name')}
                aria-invalid={!!errors.organization_name}
              />
              {errors.organization_name && (
                <p className="text-sm text-destructive">{errors.organization_name.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="admin_email">Admin Email *</Label>
              <Input
                id="admin_email"
                type="email"
                placeholder="admin@company.com"
                autoComplete="email"
                {...register('admin_email')}
                aria-invalid={!!errors.admin_email}
              />
              {errors.admin_email && (
                <p className="text-sm text-destructive">{errors.admin_email.message}</p>
              )}
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="admin_first_name">First Name (Optional)</Label>
                <Input
                  id="admin_first_name"
                  placeholder="John"
                  autoComplete="given-name"
                  {...register('admin_first_name')}
                />
                {errors.admin_first_name && (
                  <p className="text-sm text-destructive">{errors.admin_first_name.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="admin_last_name">Last Name (Optional)</Label>
                <Input
                  id="admin_last_name"
                  placeholder="Doe"
                  autoComplete="family-name"
                  {...register('admin_last_name')}
                />
                {errors.admin_last_name && (
                  <p className="text-sm text-destructive">{errors.admin_last_name.message}</p>
                )}
              </div>
            </div>
          </div>
        </form>
    </Modal>
  )
}